import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Col, Row } from "react-bootstrap";
import "./generate-report-modal.scss";
import ReportsService from "../../../../../../../../../services/reports-service";

const GenerateReportModal = ({ show, onClose, reports }) => {
    const reportsService = new ReportsService();

    const initialFormData = {
        addresseeTitle: "Mr.",
        firstName: "",
        middleInitial: "",
        lastName: "",
        designation: "",
        companyName: "",
        address: "",
        referenceNumber: "",
    };

    const [formData, setFormData] = useState(initialFormData);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [alertModal, setAlertModal] = useState({ show: false, message: "" });

    useEffect(() => {
        if (show) {
            setFormData(initialFormData);
            setShowSuccessModal(false);
            setAlertModal({ show: false, message: "" });
        }
    }, [show]);

    // Required fields validity (excluding middleInitial and referenceNumber)
    const allFilled = Object.entries(formData)
        .filter(([key]) => key !== "middleInitial" && key !== "referenceNumber")
        .every(([, val]) => val.trim().length > 0);

    // Reference number must be 1–30 chars, no spaces
    const refValid =
        formData.referenceNumber.length > 0 &&
        formData.referenceNumber.length <= 30 &&
        !/\s/.test(formData.referenceNumber);

    const isFormValid = allFilled && refValid;

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (name === "middleInitial") {
            newValue = value.toUpperCase();
        }
        if (name === "referenceNumber") {
            // strip spaces
            newValue = value.replace(/\s/g, "");
        }
        setFormData({ ...formData, [name]: newValue });
    };

    const handleSubmit = async () => {
        if (!isFormValid) {
            setAlertModal({
                show: true,
                message: "All fields and Reference Number (1–30 chars, no spaces) are required."
            });
            return;
        }

        const requestData = {
            bpId: reports[0].bpId,
            sellingAgentRequestDate: reports[0].sellingAgentRequestDate,
            addressee: [{
                addresseeTitle: formData.addresseeTitle,
                firstName: formData.firstName,
                middleInitial: formData.middleInitial,
                lastName: formData.lastName
            }],
            address: formData.address,
            companyName: formData.companyName,
            designation: formData.designation,
            referenceNumber: formData.referenceNumber
        };
        
        try {
            const response = await reportsService.rrMakerGenerateCertificationOfHoldings(
                requestData.bpId,
                requestData.sellingAgentRequestDate,
                requestData.addressee,
                requestData.address,
                requestData.companyName,
                requestData.designation,
                requestData.referenceNumber
            );
            const { message, updatedAttributes } = response;

            if (updatedAttributes) {
                onClose();
                setTimeout(() => setShowSuccessModal(true), 300);
            } else {
                setAlertModal({ show: true, message: message || "Error submitting the report." });
            }
        } catch(error){
            console.log(error);
            setAlertModal({ show: true, message: error.response?.data?.message || "Error submitting the report." });
        }
    };

    return (
        <>
            <Modal show={show} onHide={onClose} centered className="generate-report-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Generate Report</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {/* Addressee Row */}
                        <Row className="mb-3 align-items-center">
                            <Col xs="auto">
                                <Form.Label className="mb-0">Addressee *</Form.Label>
                            </Col>
                            <Col xs="auto">
                                <Form.Group>
                                    <Form.Control
                                        as="select"
                                        name="addresseeTitle"
                                        size="sm"
                                        required
                                        className="mt-3 form-select"
                                        onChange={handleChange}
                                    >
                                        <option>Mr.</option>
                                        <option>Ms.</option>
                                        <option>Mrs.</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col xs={5}>
                                <Form.Group className="pt-3">
                                    <Form.Control
                                        type="text"
                                        name="firstName"
                                        placeholder="Enter First Name *"
                                        maxLength={150}
                                        required
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs="auto">
                                <Form.Group className="pt-3">
                                    <Form.Control
                                        type="text"
                                        name="middleInitial"
                                        placeholder="M"
                                        maxLength={1}
                                        size="sm"
                                        className="middle-initial-input"
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3 align-items-center">
                            <Col xs={{ span: 4, offset: 2 }}>
                                <Form.Group>
                                    <Form.Control
                                        type="text"
                                        name="lastName"
                                        placeholder="Enter Last Name *"
                                        maxLength={150}
                                        required
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        {/* Designation */}
                        <Form.Group className="mb-3">
                            <Form.Label>Designation *</Form.Label>
                            <Form.Control
                                type="text"
                                name="designation"
                                placeholder="Enter Designation"
                                maxLength={150}
                                onChange={handleChange}
                                value={formData.designation}
                            />
                        </Form.Group>
                        {/* Company */}
                        <Form.Group className="mb-3">
                            <Form.Label>Company *</Form.Label>
                            <Form.Control
                                type="text"
                                name="companyName"
                                placeholder="Enter Company Name"
                                maxLength={150}
                                onChange={handleChange}
                                value={formData.companyName}
                            />
                        </Form.Group>
                        {/* Address */}
                        <Form.Group className="mb-3">
                            <Form.Label>Address *</Form.Label>
                            <Form.Control
                                type="text"
                                name="address"
                                placeholder="Enter Address"
                                maxLength={150}
                                onChange={handleChange}
                                value={formData.address}
                            />
                        </Form.Group>
                        {/* Reference Number */}
                        <Form.Group className="mb-3">
                            <Form.Label>Reference Number *</Form.Label>
                            <Form.Control
                                type="text"
                                name="referenceNumber"
                                placeholder="Enter Reference Number"
                                onChange={handleChange}
                                value={formData.referenceNumber}
                                isInvalid={!refValid}
                            />
                            {/* <Form.Text className={`text-${formData.referenceNumber.length <= 30 ? "muted" : "danger"}`}>
                                {formData.referenceNumber.length}/30 characters (no spaces)
                            </Form.Text> */}
                            <Form.Control.Feedback type="invalid">
                                Reference Number must be 1–30 chars with no spaces.
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid}>Confirm</Button>
                </Modal.Footer>
            </Modal>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>Report is generating. Refresh in a few minutes!</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => window.location.reload()}>OK</Button>
                </Modal.Footer>
            </Modal>

            {/* Alert Modal */}
            <Modal show={alertModal.show} onHide={() => setAlertModal({ ...alertModal, show: false })} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Alert</Modal.Title>
                </Modal.Header>
                <Modal.Body>{alertModal.message}</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setAlertModal({ ...alertModal, show: false })}>OK</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default GenerateReportModal;
